/**
 * Variant Search Filter
 * Filters variant swatches by search term
 */

class VariantSearch {
  constructor() {
    this.expandedStates = {}; // Track expanded state per fieldset
    this.init();
  }

  init() {
    // Find all variant search inputs
    document.querySelectorAll('[data-variant-search]').forEach(input => {
      input.dataset.searchInitialized = 'true';
      this.setupSearch(input);
    });

    // Setup collapsible swatches
    document.querySelectorAll('[data-swatches-toggle]').forEach(btn => {
      btn.dataset.toggleInitialized = 'true';
      this.setupCollapsible(btn);
    });

    // Setup inline color compare buttons
    document.querySelectorAll('[data-open-color-compare]').forEach(btn => {
      btn.dataset.compareInitialized = 'true';
      this.setupInlineCompare(btn);
    });

    // Prevent tooltip side-clipping in collapsed wrappers
    this.setupTooltipBoundsCheck();

    // Also handle dynamically added content (e.g., after variant change or quick view modals)
    this.observeNewSearchInputs();
  }

  setupTooltipBoundsCheck() {
    // When hovering a swatch inside a collapsed wrapper, shift the tooltip
    // horizontally just enough to keep it within the overflow:hidden boundary.
    // Uses offsetWidth (unaffected by CSS scale transition) for accuracy.
    document.addEventListener('mouseenter', (e) => {
      const swatch = e.target.closest('.color-swatch');
      if (!swatch) return;
      const wrapper = swatch.closest('.swatches-collapsible-wrapper.is-collapsed');
      if (!wrapper) return;
      const tooltip = swatch.querySelector('.tooltip');
      if (!tooltip) return;

      tooltip.style.removeProperty('--tw-translate-x');

      requestAnimationFrame(() => {
        const swatchRect = swatch.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const tooltipWidth = tooltip.offsetWidth;
        const swatchCenterX = swatchRect.left + swatchRect.width / 2;
        const tooltipLeft = swatchCenterX - tooltipWidth / 2;
        const tooltipRight = swatchCenterX + tooltipWidth / 2;

        let adjust = 0;
        if (tooltipLeft < wrapperRect.left) {
          adjust = wrapperRect.left - tooltipLeft;
        } else if (tooltipRight > wrapperRect.right) {
          adjust = wrapperRect.right - tooltipRight;
        }

        if (adjust !== 0) {
          tooltip.style.setProperty('--tw-translate-x', `calc(-50% + ${adjust}px)`);
        }
      });
    }, true);

    document.addEventListener('mouseleave', (e) => {
      const swatch = e.target.closest('.color-swatch');
      if (!swatch) return;
      const tooltip = swatch.querySelector('.tooltip');
      if (tooltip) tooltip.style.removeProperty('--tw-translate-x');
    }, true);
  }

  setupInlineCompare(btn) {
    btn.addEventListener('click', () => {
      // Find and click the main color compare button on the page
      const mainCompareBtn = document.querySelector('.colorway-compare-link, .colorway-compare-btn, [id^="open-colorway-modal"]');
      if (mainCompareBtn) {
        mainCompareBtn.click();
      }
    });
  }

  setupCollapsible(toggleBtn) {
    const fieldset = toggleBtn.closest('.variant-input-wrapper');
    if (!fieldset) return;

    const wrapper = fieldset.querySelector('[data-swatches-collapsible]');
    if (!wrapper) return;

    // Generate a unique ID for this fieldset based on option slug
    const optionSlug = fieldset.dataset.optionSlug || 'default';
    const stateKey = `swatch-expanded-${optionSlug}`;

    // Restore previous state if it was expanded
    if (this.expandedStates[stateKey]) {
      wrapper.classList.remove('is-collapsed');
      wrapper.classList.add('is-expanded');
      const showText = toggleBtn.querySelector('.toggle-show');
      const hideText = toggleBtn.querySelector('.toggle-hide');
      if (showText) showText.style.display = 'none';
      if (hideText) hideText.style.display = 'inline';
    }

    toggleBtn.addEventListener('click', () => {
      const isCurrentlyCollapsed = wrapper.classList.contains('is-collapsed');
      wrapper.classList.toggle('is-collapsed', !isCurrentlyCollapsed);
      wrapper.classList.toggle('is-expanded', isCurrentlyCollapsed);

      // Save state
      this.expandedStates[stateKey] = isCurrentlyCollapsed; // true if now expanded

      // Update button text visibility
      const showText = toggleBtn.querySelector('.toggle-show');
      const hideText = toggleBtn.querySelector('.toggle-hide');
      if (showText) showText.style.display = isCurrentlyCollapsed ? 'none' : 'inline';
      if (hideText) hideText.style.display = isCurrentlyCollapsed ? 'inline' : 'none';
    });
  }

  setupSearch(input) {
    const fieldset = input.closest('.variant-input-wrapper');
    if (!fieldset) return;

    const swatchList = fieldset.querySelector('[data-swatches-list]') || fieldset.querySelector('.swatches');
    const swatchItems = swatchList?.querySelectorAll('[data-swatch-value]');
    const clearBtn = fieldset.querySelector('[data-variant-search-clear]');
    const noResults = fieldset.querySelector('[data-variant-no-results]');

    if (!swatchItems || swatchItems.length === 0) return;

    // Debounce search
    let debounceTimer;

    input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.filterSwatches(input.value, swatchItems, clearBtn, noResults);
      }, 150);
    });

    // Clear button
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        input.value = '';
        this.filterSwatches('', swatchItems, clearBtn, noResults);
        input.focus();
      });
    }

    // Clear on Escape key
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        input.value = '';
        this.filterSwatches('', swatchItems, clearBtn, noResults);
      }
    });
  }

  filterSwatches(searchTerm, swatchItems, clearBtn, noResults) {
    const term = searchTerm.toLowerCase().trim();
    let visibleCount = 0;

    swatchItems.forEach(item => {
      const value = item.dataset.swatchValue || '';
      const matches = !term || value.includes(term);

      if (matches) {
        item.classList.remove('swatch-hidden');
        visibleCount++;
      } else {
        item.classList.add('swatch-hidden');
      }
    });

    // Toggle clear button visibility
    if (clearBtn) {
      clearBtn.classList.toggle('hidden', !term);
    }

    // Toggle no results message
    if (noResults) {
      noResults.classList.toggle('hidden', visibleCount > 0 || !term);
    }
  }

  observeNewSearchInputs() {
    // Watch for dynamically added elements (e.g., after variant change or quick view modals)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Setup search inputs
            const inputs = node.querySelectorAll?.('[data-variant-search]');
            inputs?.forEach(input => {
              if (!input.dataset.searchInitialized) {
                input.dataset.searchInitialized = 'true';
                this.setupSearch(input);
              }
            });

            // Setup collapsible swatch toggles
            const toggleBtns = node.querySelectorAll?.('[data-swatches-toggle]');
            toggleBtns?.forEach(btn => {
              if (!btn.dataset.toggleInitialized) {
                btn.dataset.toggleInitialized = 'true';
                this.setupCollapsible(btn);
              }
            });

            // Setup inline compare buttons
            const compareBtns = node.querySelectorAll?.('[data-open-color-compare]');
            compareBtns?.forEach(btn => {
              if (!btn.dataset.compareInitialized) {
                btn.dataset.compareInitialized = 'true';
                this.setupInlineCompare(btn);
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new VariantSearch());
} else {
  new VariantSearch();
}
